'use client';
import React, { useEffect } from 'react';
import { Button, Chip, Div, Skeleton, Stepper, Text } from '@pezeshk-book/ui-kit';
import Image from '@elements/image';
import Link from 'next/link';
import { DrawerMenuProps } from './drawer-menu.props';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@store/root-reducer';
import { AuthActions } from '@store/auth/auth-actions';
import routes from '@routes';
import { useParams, usePathname } from 'next/navigation';
import { useRouter } from 'next-nprogress-bar';
import dynamic from 'next/dynamic';
import { parsePrice } from '@utils/helpers/parse-price';
import getParseRoute from '@utils/helpers/parse-route';
import getDictionary from '@dictionaries-client';
import dictionaryParser from '@utils/helpers/dictionary-parser';
import { LocaleTypes } from '@dictionaries-types';

const Icons = {
  USERS: 'users',
  PRODUCTS: 'products',
  LABS: 'labs',
  PHARMACIES: 'pharmacies',
  HOME_CARE: 'home-care',
  POSTS: 'posts',
  FAQ: 'faq',
  ABOUT_US: 'about-us',
  CONTACTS: 'contacts',
};

const DrawerMenu = (props: DrawerMenuProps) => {
  const router = useRouter();
  const pathname = usePathname();
  const dispatch = useDispatch();
  const { lang } = useParams<{lang: LocaleTypes}>();
  const { home: { header: { drawer } } } = getDictionary(lang);

  const { onClick, items, menuCallBack, openDrawer } = props;
  const { isLoggedIn, userInfo, walletCredit, walletCreditLoading } = useSelector((state: RootState) => state.auth);

  useEffect(() => {
    if (openDrawer && !walletCredit && isLoggedIn) {
      dispatch(AuthActions.getProfileClientInfo());
    }
  }, [openDrawer]);

  const myOrdersHandler = () => {
    menuCallBack();
    if (isLoggedIn) {
      router.push(getParseRoute({ pathname: routes['route.client.orders'], locale: lang }));
    } else {
      dispatch(AuthActions.setLoginModal({ loginModal: true, redirectRoute: getParseRoute({ pathname: routes['route.client.orders'], locale: lang }) }));
    }
  };

  const handleLoginModal = () => {
    menuCallBack();
    dispatch(AuthActions.setLoginModal({ loginModal: true }));
  };

  const handleStepperClick = () => {
    onClick();
    router.push(getParseRoute({ pathname: routes['route.client.wallet'], locale: lang }));
  };

  return (
    <Div className={'flex-col items-start w-full gap-y-8'}>
      <Div className={'w-full bg-darkBlue-50 flex-col items-center pb-3 py-6 relative gap-y-1'}>
        <Div className={'w-11/12 items-start justify-between'}>
          <Div className={'w-16 h-16 min-w-16 min-h-16 relative'}>
            <Image alt={drawer.profile_alt} src={'/images/profile-image.webp'} fill={true} className={'object-contain'}/>
          </Div>
          <Div className={'relative items-center justify-center'}>
            <Button color={'secondary'} onClick={myOrdersHandler} rounded={'full'} size={'small'}>
              {drawer.my_orders}
            </Button>
          </Div>
        </Div>
        <Div className={'w-11/12 items-start flex-col'}>
          {isLoggedIn ? (
            <>
              <Div className={'gap-1'}>
                <Text color={'grey.600'} typography={'sm'}>{drawer.dear_client}</Text>
                <Text color={'grey.600'} typography={'sm'}>{isLoggedIn ? userInfo : drawer.not_registered}</Text>
              </Div>
              <Button href={getParseRoute({ pathname: '/client-dashboard', locale: lang })} onClick={onClick} className={'!p-0'} variant={'text'} color={'primary'}>
                {drawer.edit_profile}
              </Button>
              <Div className={'w-full items-center justify-around gap-5 mt-4'}>
                {walletCreditLoading ? (
                  <Skeleton color={'control'} className={'w-full'} size={'small'} shape={'rounded'}/>
                ) : (
                  <Button onClick={handleStepperClick} color={'tertiary'} rounded={'full'} size={'small'} className={'w-full justify-around items-center'}>
                    <Text typography={'sm'} color={'white'}>
                      {drawer.credit}
                    </Text>
                    <Text color={'white'} typography={'sm'}>{dictionaryParser({ content: drawer.credit_value, variables: [{ credit: parsePrice(walletCredit?.total) }] })}</Text>
                  </Button>
                )}
                <Stepper onClick={handleStepperClick} size={'small'} rounded={'full'} color={'tertiary'}/>
              </Div>
            </>
          ) : (
            <Button onClick={handleLoginModal} className={'!p-0'} variant={'text'} color={'tertiary'}>
              {drawer.login}
            </Button>
          )}
        </Div>
      </Div>
      <Div className={'w-full items-center justify-center flex-col'}>
        {items.map((item: any, index: any) => {
          const isCurrent = pathname.indexOf(item?.url) !== -1;

          let dynamicIcon: string = 'add-icon';

          switch (item?.slug) {
            case Icons.USERS:
              dynamicIcon = `${Icons.USERS}-icon`;
              break;
            case Icons.LABS:
              dynamicIcon = `${Icons.LABS}-icon`;
              break;
            case Icons.ABOUT_US:
              dynamicIcon = `${Icons.ABOUT_US}-icon`;
              break;
            case Icons.HOME_CARE:
              dynamicIcon = `${Icons.HOME_CARE}-icon`;
              break;
            case Icons.CONTACTS:
              dynamicIcon = `${Icons.CONTACTS}-icon`;
              break;
            case Icons.PHARMACIES:
              dynamicIcon = `${Icons.PHARMACIES}-icon`;
              break;
            case Icons.POSTS:
              dynamicIcon = `${Icons.POSTS}-icon`;
              break;
            case Icons.PRODUCTS:
              dynamicIcon = `${Icons.PRODUCTS}-icon`;
              break;
            case Icons.FAQ:
              dynamicIcon = `${Icons.FAQ}-icon`;
              break;
          }

          const DynamicComponent = dynamic(() => import(`../../../../assets/icons/${dynamicIcon}`));
          if (!item.isActive) {
            return null;
          }
          return (
            <Link
              passHref={true}
              className={'flex-col w-full items-end flex relative'}
              key={`drawer_${index}`}
              href={getParseRoute({ pathname: item?.url, locale: lang })}>
              <Div onClick={onClick} className={'w-11/12 h-10 my-1 bg-darkBlue-50 rounded px-4 items-center self-center justify-between'}>
                <Button
                  disabled={item.disabled}
                  className={'!p-0'}
                  color={isCurrent ? 'tertiary' : 'control'}
                  startAdornment={<DynamicComponent />}
                  variant={'text'}>
                  {item.title}
                </Button>
                {item?.isNew ? (
                  <Chip color={'tertiary'} size={'small'} variant={'reverse'} value={drawer.chips.new}/>
                ) : null}
                {item?.hasInsurance ? (
                  <Chip color={'secondary'} size={'small'} variant={'reverse'} value={drawer.chips.insurance}/>
                ) : null}
                {item?.comingSoon ? (
                  <Chip color={'danger'} size={'small'} variant={'reverse'} value={drawer.chips.coming_soon}/>
                ) : null}
              </Div>
            </Link>
          );
        })}
      </Div>
      <Div className={'items-center justify-center w-full mb-3 gap-2'}>
        {/*<Div className={'w-9 h-9 relative object-contain'}>
          <Image src={SupportIcon} alt={'support'}/>
        </Div>*/}
        <Text color={'grey.600'} typography={'sm'}>
          {drawer.call_support}
        </Text>
        <Text color={'grey.600'} typography={'sm'}>
          <a href="tel:+14389884884">(438) 988-4884</a>
        </Text>
      </Div>
    </Div>
  );
};

export default DrawerMenu;
